/** Lowest authorable row: row 1 maps to position 0, which the grid never renders. */
const MIN_PLACEMENT_ROW = 2;

const BREAKPOINTS = ['unprefixed', 'sm', 'md', 'lg', 'xl'];

/**
 * Maps a 1-indexed placement row to the 0-indexed per-breakpoint position where the grid
 * inserts the slot — after `position * productsPerRow` products (productsPerRow = 1/2/2/3/4
 * for unprefixed/sm/md/lg/xl). `unprefixedPodsPerRow` scales the unprefixed index only: there
 * the grid injects 1-up while pods may render N-up, so scaling lands the slot after a full row.
 *
 * @param {object} params
 * @param {number} params.row - 1-indexed grid row.
 * @param {number} [params.unprefixedPodsPerRow=1] - visual pods per row at the unprefixed breakpoint.
 * @returns {{ unprefixed: number, sm: number, md: number, lg: number, xl: number }} per-breakpoint position.
 */
const rowToPosition = ({ row, unprefixedPodsPerRow = 1 }) => {
  const index = row - 1;
  return {
    unprefixed: index * unprefixedPodsPerRow, sm: index, md: index, lg: index, xl: index
  };
};

/**
 * Whether a candidate position collides with any placed slot. Two slots collide when they
 * resolve to the same index at any breakpoint (the grid resolves each as
 * `position[bp] || position.unprefixed`).
 *
 * @param {Array<{ position: object }>} slots - already-placed slots.
 * @param {object} position - candidate per-breakpoint position.
 * @returns {boolean}
 */
const positionTaken = (slots, position) => slots.some((slot) => BREAKPOINTS.some(
  (bp) => (slot.position?.[bp] || slot.position?.unprefixed) === (position[bp] || position.unprefixed)
));

/**
 * Builds the `{ position, component }` slots for the given placements. A placement renders when
 * its componentName is in the registry, its row is an integer >= MIN_PLACEMENT_ROW, and that
 * componentName has no slot yet — one slot per componentName, claimed in authored order. On a
 * collision it is dropped so existing content wins.
 *
 * @param {object} params
 * @param {Array<{ componentName: string, row: number }>} [params.placements] - placements to render.
 * @param {Array<{ position: object, component: * }>} [params.existingSlots] - slots already in the grid.
 * @param {Object<string, () => *>} [params.registry={}] - componentName -> factory returning the component.
 * @param {number} [params.unprefixedPodsPerRow=1] - visual pods per row at the unprefixed breakpoint.
 * @returns {Array<{ position: object, component: * }>} the newly-added slots.
 */
export const getInGridPlacementSlots = ({
  placements,
  existingSlots,
  registry = {},
  unprefixedPodsPerRow = 1
} = {}) => {
  const placementList = Array.isArray(placements) ? placements : [];
  const placed = Array.isArray(existingSlots) ? [...existingSlots] : [];
  const added = [];
  const slottedComponentNames = new Set();

  placementList.forEach((placement) => {
    const factory = registry[placement?.componentName];
    if (!factory || !Number.isInteger(placement?.row) || placement.row < MIN_PLACEMENT_ROW) {
      return;
    }

    if (slottedComponentNames.has(placement.componentName)) {
      return;
    }

    const position = rowToPosition({ row: placement.row, unprefixedPodsPerRow });

    if (positionTaken(placed, position)) {
      return;
    }

    const slot = { position, component: factory() };
    slottedComponentNames.add(placement.componentName);
    placed.push(slot);
    added.push(slot);
  });

  return added;
};
